import * as RadixSelect from '@radix-ui/react-select';
import { FC, forwardRef, Fragment, ReactElement, ReactNode } from 'react';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { FaIcon } from './FaIcon';
import { cn } from '../utils';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

export type SelectItem =
  | {
      icon?: IconDefinition;
      value: string;
      label: string;
    }
  | {
      icon?: IconDefinition;
      value: string;
      label: string;
    }[];

type SelectProps = RadixSelect.SelectProps & {
  items: SelectItem[];
  onSelect?: (value: string) => void;
  container: HTMLElement;
  trigger: ReactElement;
  contentClassName?: string;
  itemClassName?: string;
  contentSettings?: RadixSelect.SelectContentProps;
};

export const Select: FC<SelectProps> = ({
  items,
  onSelect,
  container,
  trigger,
  contentClassName,
  defaultValue,
  itemClassName,
  contentSettings,
  ...rest
}) => {
  return (
    <RadixSelect.Root
      defaultValue={defaultValue}
      onValueChange={(value) => {
        onSelect?.(value);
      }}
      {...rest}
    >
      <RadixSelect.Trigger asChild>{trigger}</RadixSelect.Trigger>
      <RadixSelect.Portal container={container}>
        <RadixSelect.Content
          position="popper"
          avoidCollisions
          side="bottom"
          align="end"
          collisionPadding={12}
          {...(contentSettings || {})}
          className={cn(
            'flex min-w-[var(--radix-select-trigger-width)] flex-col gap-y-2 rounded-xl bg-neutral-0 shadow-100',
            contentClassName,
          )}
        >
          <RadixSelect.Viewport className="p-2">
            {items.map((item) => {
              if (Array.isArray(item)) {
                return (
                  <Fragment key={`fragment-${item[0].value}}`}>
                    <RadixSelect.Separator className="my-1 h-px bg-neutral-100" />
                    <RadixSelect.Group key={`group-${item[0].value}`}>
                      {item.map(({ value, label }) => (
                        <SelectItem
                          className={itemClassName}
                          key={value}
                          value={value}
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </RadixSelect.Group>
                  </Fragment>
                );
              }

              return (
                <SelectItem
                  className={itemClassName}
                  key={item.value}
                  value={item.value}
                  icon={item.icon}
                >
                  {item.label}
                </SelectItem>
              );
            })}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
};

const SelectItem = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    className?: string;
    icon?: IconDefinition;
    value: string;
  }
>(({ children, className, icon, ...props }, forwardedRef) => {
  return (
    <RadixSelect.Item
      className={cn(
        'flex flex-1 cursor-pointer items-center justify-start gap-3 rounded-lg px-4 py-2.5 text-left text-title-sm  text-neutral-500 hover:bg-neutral-200 hover:outline-none data-[state=checked]:text-prominence-500',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      {icon && <FaIcon icon={icon} />}
      <RadixSelect.ItemText asChild>
        <p>{children}</p>
      </RadixSelect.ItemText>
      <RadixSelect.ItemIndicator>
        <FaIcon
          className="text-prominence-500"
          width={16}
          height={16}
          icon={faCheck}
        />
      </RadixSelect.ItemIndicator>
    </RadixSelect.Item>
  );
});

SelectItem.displayName = 'SelectItem';
